"use client";

import { useState, useEffect } from "react";
import config from "../../config/config";
import { Skeleton } from "../ui/skeleton";

export default function CategoryList({
  postType,
  selectedCategory,
  onCategoryChange,
  loading,
}) {
  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const response = await fetch(
          `${config.NEXT_PUBLIC_API_URL}/api/public/categories/by-type/${postType}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch categories for ${postType}`);
        }

        const result = await response.json();

        if (result.success) {
          setCategories(result.data.categories);
        } else {
          throw new Error(
            result.message || `Failed to fetch categories for ${postType}`
          );
        }
      } catch (err) {
        console.error(`Error fetching categories for ${postType}:`, err);
        setError(err.message);
      } finally {
        setCategoryLoading(false);
      }
    };

    if (postType) {
      fetchCategories();
    }
  }, [postType]);

  if (categoryLoading) {
    return (
      <div className="mb-8 pb-0">
        <div className="relative">
          <div className="flex overflow-x-auto no-scrollbar py-1">
            <div className="flex space-x-10 px-1 pb-1">
              {/* Skeleton for "ALL" button */}
              <Skeleton className="h-9 w-16" />

              {/* Skeleton for category buttons */}
              {[1, 2, 3, 4, 5].map((item) => (
                <Skeleton key={item} className="h-9 w-28" />
              ))}
            </div>
          </div>
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return null; // Don't show anything if there's an error
  }

  if (categories.length === 0) {
    return null; // Don't show anything if there are no categories
  }

  return (
    <div className="mb-8 pb-0">
      <div className="relative">
        {/* Modern pill-style category buttons */}
        <div className="flex overflow-x-auto no-scrollbar py-3">
          <div className="flex space-x-3 px-1">
            <button
              onClick={() => onCategoryChange(null)}
              className={`font-semibold text-sm xl:text-base whitespace-nowrap px-5 py-2.5 rounded-full transition-all duration-300 border-2 ${
                selectedCategory === null
                  ? "bg-primary text-white border-primary shadow-lg transform scale-105"
                  : "bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary hover:shadow-md"
              }`}
              disabled={loading}
            >
              All Categories
            </button>

            {categories.map((category) => (
              <button
                key={category._id}
                onClick={() => onCategoryChange(category.slug)}
                className={`font-semibold text-sm xl:text-base whitespace-nowrap px-5 py-2.5 rounded-full transition-all duration-300 border-2 flex items-center ${
                  selectedCategory === category.slug
                    ? "bg-primary text-white border-primary shadow-lg transform scale-105"
                    : "bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary hover:shadow-md"
                }`}
                disabled={loading}
              >
                <span className="truncate max-w-[120px] sm:max-w-[150px] md:max-w-[200px] lg:max-w-none">
                  {category.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
